import { getPeg, getImage, checkHotspots } from "@/services/api.service";
import { mapGetters, mapActions } from "vuex";
import {
	NavButton,
	PortfolioToggleButton,
	OffscreenHotspotsIndicator,
	PegComponent,
	GridControls,
	FiltersMenu,
	InfoCard,
	CategoryModal,
	EmptyStateModal,
	InfoModal,
	ValueDriverModal,
	LanguageModal,
	SearchModal,
	HelpModal,
	ShareModal,
} from "@/components/ui";
import ErrorBlock from "@/components/screens/home-screen/components/ErrorBlock.vue";
import { robotsService } from "@/services/robots.service";
import { FILTER_CONFIG, LAYER_ID_TO_SLUG } from "@/constants/filter.constants";

const MAX_ZOOM_TIMES = 5;
const TOTAL_ZOOM_STEPS = 4;
const ZOOM_ANIMATION_DURATION = 300;

export default {
	name: "GridScreen",
	components: {
		PegComponent,
		GridControls,
		HelpModal,
		ShareModal,
		InfoCard,
		FiltersMenu,
		SearchModal,
		LanguageModal,
		InfoModal,
		ValueDriverModal,
		CategoryModal,
		EmptyStateModal,
		PortfolioToggleButton,
		OffscreenHotspotsIndicator,
		NavButton,
		ErrorBlock,
	},

	data() {
		return {
			monitor_fps: 60,
			last_frame_time: performance.now(),
			inertia_timeout: null,
			is_dragging: false,
			last_drag_x_offset: 0,
			last_drag_y_offset: 0,
			drag_x_offset: 0,
			drag_y_offset: 0,
			original_image: null,
			blur_hash_image: null,
			original_image_width: 0,
			original_image_height: 0,
			source_image_width: 0,
			source_image_height: 0,
			original_image_aspect_ratio: 0,
			current_image_width: 0,
			current_image_height: 0,
			zoom_level: 1,
			min_zoom: 0,
			max_zoom: 1,
			zoom_step: 0.1,
			x_inertia: 0,
			y_inertia: 0,
			resize_timeout: null,
			loading: true,
			image_ready: false,
			blur_loaded: false,
			show_notice: false,
			has_placeholder: false,
			driverModalVisible: false,
			valueDriverId: "",
			offscreen_top: 0,
			offscreen_bottom: 0,
			offscreen_left: 0,
			offscreen_right: 0,
			show_search_modal: false,
			show_language_modal: false,
			show_share_modal: false,
			show_category_modal: false,
			show_info_modal: false,
			show_empty_state_modal: false,
			visible_hotspots: [],
			hotspots: [],
			is_open: false,
			loadedCategory: null,
			loadedSector: null,
		};
	},

	mounted() {
		this.setupFrameLoop();
		this.setupEventListeners();
		this.initialiseScreen();
	},

	beforeUnmount() {
		this.cleanup();
	},

	computed: {
		...mapGetters("processes", [
			"currentCategory",
			"currentSector",
			"apiStatus",
			"errorMessage",
		]),
		...mapGetters(["activeLayer"]),
		...mapGetters("portfolios", ["activePortfolio"]),
		...mapGetters("languages", ["activeLanguage"]),
		...mapGetters("valueDrivers", ["activeValueDrivers"]),
		...mapGetters("staticContent", ["getTranslations"]),

		roomTitle() {
			return this.currentSector?.title;
		},
		isMdUp() {
			return window.matchMedia("(min-width: 768px)").matches;
		},
		hasInnovationHotspots() {
			return this.hotspots.some((hotspot) => hotspot.portfolio === 7);
		},
		hasWaterSavingHotspots() {
			return this.hotspots.some((hotspot) => hotspot.portfolio === 8);
		},
		isSolutionsLayer() {
			const id = this.activeLayer?.id;
			return id === FILTER_CONFIG.SOLUTIONS_LAYER_ID || id === "solutions";
		},
	},

	watch: {
		"$route.params.category"() {
			this.initialiseScreen();
		},
		"$route.params.sector"() {
			this.initialiseScreen();
		},
		apiStatus(newStatus) {
			// Set noindex when there's an error (not found)
			if (newStatus === "error" || newStatus === "failed") {
				robotsService.setNoIndex();
			} else {
				// Remove noindex when error is cleared
				robotsService.allowIndex();
			}
		},
		activeLayer: {
			handler(newLayer, oldLayer) {
				if (newLayer?.id !== oldLayer?.id) {
					this.handleFilterChange();
				}
			},
			deep: true,
		},
		activePortfolio: {
			handler(newPortfolio, oldPortfolio) {
				if (newPortfolio?.id !== oldPortfolio?.id) {
					this.updateVisibleHotspots();
				}
			},
			deep: true,
		},
		show_language_modal(newVal) {
			this.dispatchModalStateChange(newVal);
		},
		show_category_modal(newVal) {
			this.dispatchModalStateChange(newVal);
		},
		show_empty_state_modal(newVal) {
			this.dispatchModalStateChange(newVal);
		},
	},

	methods: {
		...mapActions("portfolios", ["setPortfolios", "setActivePortfolio"]),
		...mapActions("languages", ["setLanguages", "setActiveLanguage"]),
		...mapActions("valueDrivers", ["setValueDrivers", "setActiveValueDrivers"]),

		setupFrameLoop() {
			this.inertia_timeout = setInterval(() => this.applyInertia(), 1000 / 60);
			requestAnimationFrame(this.calcualteMontorFps);
		},

		setupEventListeners() {
			document.addEventListener("modal-state-change", this.handleModalEvent);
			this.handleModalStateChange(false);
		},

		cleanup() {
			clearInterval(this.inertia_timeout);
			clearTimeout(this.resize_timeout);
			document.removeEventListener("modal-state-change", this.handleModalEvent);
			this.removeEventListeners();
			// Remove noindex meta tag on cleanup
			robotsService.allowIndex();
		},

		handleModalEvent(event) {
			this.is_open = event.detail.isOpen;
			this.handleModalStateChange(this.is_open);
		},

		handleModalStateChange(is_open) {
			if (is_open) {
				this.removeEventListeners();
			} else {
				this.addEventListeners();
			}
		},

		addEventListeners() {
			document.body.addEventListener("mousedown", this.onMouseDown, true);
			document.body.addEventListener("mouseup", this.onMouseUp, true);
			document.body.addEventListener("mousemove", this.onMouseMove, true);
			document.body.addEventListener("wheel", this.onMouseWheel, true);
			document.body.addEventListener("touchend", this.onTouchEnd, true);
			document.body.addEventListener("touchstart", this.onTouchStart);
			document.body.addEventListener("touchmove", this.onTouchMove);
			window.addEventListener("resize", this.onWindowResize);
		},

		removeEventListeners() {
			document.body.removeEventListener("mousedown", this.onMouseDown, true);
			document.body.removeEventListener("mouseup", this.onMouseUp, true);
			document.body.removeEventListener("mousemove", this.onMouseMove, true);
			document.body.removeEventListener("wheel", this.onMouseWheel, true);
			document.body.removeEventListener("touchend", this.onTouchEnd, true);
			document.body.removeEventListener("touchstart", this.onTouchStart);
			document.body.removeEventListener("touchmove", this.onTouchMove);
			window.removeEventListener("resize", this.onWindowResize);
		},

		calcualteMontorFps() {
			this.monitor_fps = Math.round(
				1000 / (performance.now() - this.last_frame_time),
			);
			this.last_frame_time = performance.now();
			requestAnimationFrame(this.calcualteMontorFps);
		},

		onMouseDown(e) {
			this.is_dragging = true;
			this.last_drag_x_offset = e.clientX;
			this.last_drag_y_offset = e.clientY;
		},

		onMouseUp() {
			this.is_dragging = false;
		},

		onMouseMove(e) {
			if (!this.is_dragging) return;

			this.drag_x_offset += e.clientX - this.last_drag_x_offset;
			this.drag_y_offset += e.clientY - this.last_drag_y_offset;
			this.x_inertia = e.clientX - this.last_drag_x_offset;
			this.y_inertia = e.clientY - this.last_drag_y_offset;
			this.last_drag_x_offset = e.clientX;
			this.last_drag_y_offset = e.clientY;

			this.updateImageSizeAndShape();
		},

		onTouchStart(event) {
			const touch = event.touches[0] || event.changedTouches[0];
			this.last_drag_x_offset = touch.clientX;
			this.last_drag_y_offset = touch.clientY;
			this.is_dragging = true;
		},

		onTouchMove(event) {
			if (!this.is_dragging) return;

			const touch = event.touches[0] || event.changedTouches[0];
			this.drag_x_offset += touch.clientX - this.last_drag_x_offset;
			this.drag_y_offset += touch.clientY - this.last_drag_y_offset;
			this.x_inertia = touch.clientX - this.last_drag_x_offset;
			this.y_inertia = touch.clientY - this.last_drag_y_offset;
			this.last_drag_x_offset = touch.clientX;
			this.last_drag_y_offset = touch.clientY;

			this.updateImageSizeAndShape();
		},

		onTouchEnd() {
			this.is_dragging = false;
		},

		onMouseWheel(e) {
			const delta = Math.sign(e.deltaY) / 30;
			const amount = delta * this.zoom_step;
			this.applyZoomChange(amount);
		},

		onWindowResize() {
			clearTimeout(this.resize_timeout);
			this.resize_timeout = setTimeout(() => {
				this.updateZoomConfig();
				this.updateImageSizeAndShape();
			}, 100);
		},

		updateZoom(amount) {
			const zoom_frames = this.remap(
				ZOOM_ANIMATION_DURATION,
				1,
				1000,
				1,
				this.monitor_fps,
			);
			const step_amount = amount / zoom_frames;

			for (let i = 0; i < zoom_frames; i++) {
				setTimeout(
					() => this.applyZoomChange(step_amount),
					(ZOOM_ANIMATION_DURATION / zoom_frames) * i,
				);
			}
		},

		applyZoomChange(amount) {
			const last_width = this.current_image_width;
			const last_height = this.current_image_height;

			this.zoom_level = Math.min(
				this.max_zoom,
				Math.max(this.min_zoom, this.zoom_level - amount),
			);
			this.updateImageSizeAndShape();

			const viewport_width = window.innerWidth;
			const center_of_viewport_on_image_x =
				Math.abs(this.drag_x_offset) + viewport_width / 2;
			const x_percentage =
				center_of_viewport_on_image_x / this.current_image_width;
			const changed_size_x = this.current_image_width - last_width;
			const x_addition = changed_size_x * x_percentage;
			this.drag_x_offset -= x_addition;

			const viewport_height = window.innerHeight;
			const center_of_viewport_on_image_y =
				Math.abs(this.drag_y_offset) + viewport_height / 2;
			const y_percentage =
				center_of_viewport_on_image_y / this.current_image_height;
			const changed_size_y = this.current_image_height - last_height;
			const y_addition = changed_size_y * y_percentage;
			this.drag_y_offset -= y_addition;

			this.updateImageSizeAndShape();
		},

		applyInertia() {
			if (this.is_dragging) return;

			let something_changed = false;

			if (Math.abs(this.x_inertia) > 0.1) {
				this.x_inertia *= 0.9;
				this.drag_x_offset += this.x_inertia;
				something_changed = true;
			} else {
				this.x_inertia = 0;
			}

			if (Math.abs(this.y_inertia) > 0.1) {
				this.y_inertia *= 0.9;
				this.drag_y_offset += this.y_inertia;
				something_changed = true;
			} else {
				this.y_inertia = 0;
			}

			if (something_changed) {
				this.updateImageSizeAndShape();
			}
		},

		updateImageSizeAndShape() {
			this.current_image_width = this.original_image_width * this.zoom_level;
			this.current_image_height =
				this.current_image_width / this.original_image_aspect_ratio;

			this.drag_x_offset = Math.min(this.drag_x_offset, 0);
			this.drag_x_offset = Math.max(
				this.drag_x_offset,
				window.innerWidth - this.current_image_width,
			);
			this.drag_y_offset = Math.min(this.drag_y_offset, 0);
			this.drag_y_offset = Math.max(
				this.drag_y_offset,
				window.innerHeight - this.current_image_height,
			);

			this.updateOffscreenIndicators();
		},

		getImageStyle() {
			const style = {
				transform: `translate3d(${this.drag_x_offset}px, ${this.drag_y_offset}px, 0)`,
				width: `${this.current_image_width}px`,
				height: `${this.current_image_height}px`,
				aspectRatio: `${this.original_image_aspect_ratio}`,
				position: "relative",
			};

			if (this.blur_hash_image) {
				style.backgroundImage = `url(${this.blur_hash_image})`;
				style.backgroundSize = "cover";
				style.backgroundPosition = "center";
				style.backgroundRepeat = "no-repeat";
			} else if (this.original_image) {
				style.backgroundImage = `url(${this.original_image})`;
			} else {
				style.backgroundColor = "#000";
			}

			if (!this.image_ready) {
				style.filter = "blur(10px)";
			}

			return style;
		},

		getMainImageStyle() {
			const style = {
				position: "absolute",
				top: 0,
				left: 0,
				width: "100%",
				height: "100%",
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
				opacity: this.image_ready ? 1 : 0,
				transition: "opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
				willChange: "opacity",
			};

			if (this.image_ready && this.original_image) {
				style.backgroundImage = `url(${this.original_image})`;
			}

			return style;
		},

		updateZoomConfig() {
			const screen_width = window.innerWidth;
			const screen_height = window.innerHeight;

			if (this.source_image_width > screen_width) {
				this.current_image_width = screen_width;
				this.current_image_height =
					screen_width / this.original_image_aspect_ratio;
			} else {
				this.current_image_width = screen_width;
				this.current_image_height =
					screen_width / this.original_image_aspect_ratio;
			}

			if (this.current_image_height < screen_height) {
				this.current_image_height = screen_height;
				this.current_image_width =
					screen_height * this.original_image_aspect_ratio;
			}

			this.zoom_level = this.current_image_width / this.original_image_width;
			this.min_zoom = this.zoom_level;
			this.max_zoom = this.zoom_level * MAX_ZOOM_TIMES;
			this.zoom_step = (this.max_zoom - this.min_zoom) / TOTAL_ZOOM_STEPS;
		},

		async onGetImage(image) {
			if (!image.src) {
				this.setupPlaceholder();
				return;
			}

			this.original_image = image.src;
			this.original_image_width = image.width;
			this.original_image_height = image.height;
			this.source_image_width = image.width;
			this.source_image_height = image.height;
			this.original_image_aspect_ratio = image.width / image.height;

			if (image.blur_hash) {
				this.blur_hash_image = image.blur_hash;
				this.blur_loaded = true;
			}

			this.updateZoomConfig();
			this.updateImageSizeAndShape();

			const img = new Image();
			img.loading = "eager";
			img.onload = () => {
				this.image_ready = true;
				this.loading = false;
			};
			img.onerror = () => {
				this.image_ready = true;
				this.loading = false;
			};
			img.src = image.src;

			this.loadHotspots();
		},

		setupPlaceholder() {
			this.original_image = "/images/layout/placeholder-bg.png";
			this.original_image_width = window.innerWidth;
			this.original_image_height = window.innerHeight;
			this.source_image_width = window.innerWidth;
			this.source_image_height = window.innerHeight;
			this.original_image_aspect_ratio = 1;
			this.has_placeholder = true;
			this.image_ready = true;
			this.updateZoomConfig();
			this.updateImageSizeAndShape();
			this.loading = false;
		},

		async loadHotspots() {
			try {
				const hotspots = await getPeg(this.$route.params.sector);
				this.onGetHotspots(hotspots);
			} catch (error) {
				console.error("Failed to load hotspots:", error);
			}
		},

		onGetHotspots(payload) {
			const list = Array.isArray(payload)
				? payload
				: payload?.data ?? payload?.hotspots ?? [];
			this.hotspots = list.map((hotspot) => {
				const layer = this.normalizeHotspotLayer(hotspot);
				return { ...hotspot, layer, show: true };
			});

			this.$store.commit("hotspots/setHotspots", this.hotspots);
			this.updateVisibleHotspots();
		},

		normalizeHotspotLayer(hotspot) {
			if (hotspot.layer_id != null && LAYER_ID_TO_SLUG[hotspot.layer_id]) {
				return LAYER_ID_TO_SLUG[hotspot.layer_id];
			}
			if (hotspot.layer === "solutions" || hotspot.layer === "solution") {
				return FILTER_CONFIG.SOLUTIONS_LAYER_ID;
			}
			return hotspot.layer ?? null;
		},

		updateVisibleHotspots() {
			this.visible_hotspots = this.hotspots.filter(this.isHotspotVisible);
			this.updateOffscreenIndicators();
		},

		isHotspotVisible(hotspot) {
			if (!this.isActiveLayer(hotspot)) {
				return false;
			}

			if (this.isSolutionsLayer) {
				return (
					this.isActivePortfolio(hotspot) && this.isActiveValueDriver(hotspot)
				);
			}

			return true;
		},

		isActiveLayer(hotspot) {
			const activeId = this.activeLayer?.id;
			if (hotspot.layer === activeId) return true;
			if (activeId === "solutions" && hotspot.layer === FILTER_CONFIG.SOLUTIONS_LAYER_ID) return true;
			if (activeId === FILTER_CONFIG.SOLUTIONS_LAYER_ID && hotspot.layer === "solutions") return true;
			return false;
		},

		isActivePortfolio(hotspot) {
			const activePortfolio = this.activePortfolio;

			if (!activePortfolio || hotspot.portfolio === 1) {
				return true;
			}

			if (
				(activePortfolio?.name ?? "").toLowerCase() === "all" ||
				(activePortfolio?.name ?? "").toLowerCase() === "chemicals"
			) {
				return true;
			}

			return hotspot.portfolio === activePortfolio.id;
		},

		shouldReloadHotspots() {
			if (!this.hotspots || this.hotspots.length === 0) {
				return true;
			}

			const hasMatchingLayerHotspots = this.hotspots.some((hotspot) =>
				this.isActiveLayer(hotspot),
			);

			return !hasMatchingLayerHotspots;
		},

		isActiveValueDriver(hotspot) {
			if (!this.activeValueDrivers || this.activeValueDrivers.length === 0) {
				return true;
			}
			const hotspotDrivers = hotspot.drivers ?? [];
			if (hotspotDrivers.length === 0) return true;
			return this.activeValueDrivers.some((driver) =>
				hotspotDrivers.some((d) => d == driver.id),
			);
		},

		async handleFilterChange() {
			await this.$nextTick();
			await new Promise((resolve) => setTimeout(resolve, 50));

			if (this.shouldReloadHotspots()) {
				await this.loadHotspots();
			} else {
				this.updateVisibleHotspots();
			}

			await this.$nextTick();
			this.$forceUpdate();
		},

		updateOffscreenIndicators() {
			if (this.visible_hotspots.length === 0) return;

			if (isNaN(this.drag_x_offset)) this.drag_x_offset = 0;
			if (isNaN(this.drag_y_offset)) this.drag_y_offset = 0;

			const center_x = window.innerWidth / 2 + Math.abs(this.drag_x_offset);
			const center_y = window.innerHeight / 2 + Math.abs(this.drag_y_offset);

			const x_threshold = window.innerWidth / 2.5;
			const y_threshold = window.innerHeight / 2.5;

			this.offscreen_top = 0;
			this.offscreen_bottom = 0;
			this.offscreen_left = 0;
			this.offscreen_right = 0;

			for (let i = 0; i < this.visible_hotspots.length; i++) {
				const hotspot = this.visible_hotspots[i];
				const hotspot_offset_x = hotspot.x * this.current_image_width;
				const hotspot_offset_y = hotspot.y * this.current_image_height;

				const x_distance = Math.abs(center_x - hotspot_offset_x);
				const y_distance = Math.abs(center_y - hotspot_offset_y);

				this.visible_hotspots[i].show =
					x_distance < x_threshold && y_distance < y_threshold;

				if (!hotspot.show && this.isActiveLayer(hotspot)) {
					const viewport_left = Math.abs(this.drag_x_offset);
					const viewport_right = viewport_left + window.innerWidth;
					const viewport_top = Math.abs(this.drag_y_offset);
					const viewport_bottom = viewport_top + window.innerHeight;

					if (hotspot_offset_x < viewport_left) {
						this.offscreen_left++;
					} else if (hotspot_offset_x > viewport_right) {
						this.offscreen_right++;
					} else if (hotspot_offset_y < viewport_top) {
						this.offscreen_top++;
					} else if (hotspot_offset_y > viewport_bottom) {
						this.offscreen_bottom++;
					}
				}
			}
		},

		toggleLanguageModal() {
			this.show_language_modal = !this.show_language_modal;
		},

		toggleShareModal() {
			this.show_share_modal = !this.show_share_modal;
		},

		toggleCategoryModal() {
			this.show_category_modal = !this.show_category_modal;

			if (
				!this.show_category_modal &&
				!this.loading &&
				this.hotspots.length === 0
			) {
				this.$nextTick(() => {
					this.show_empty_state_modal = true;
				});
			}
		},

		toggleInfoModal() {
			this.show_info_modal = !this.show_info_modal;
		},

		openHelpModal() {
			this.show_notice = true;
		},
		goBack() {
			this.$router.push("/");
		},

		showNotice() {
			const hasShownNotice = localStorage.getItem("hasShownNotice") === "true";
			if (!hasShownNotice) {
				this.show_notice = true;
				localStorage.setItem("hasShownNotice", "true");
			}
		},

		help() {
			this.show_notice = true;
		},

		openDriverModal(valueDriverId) {
			this.driverModalVisible = true;
			this.valueDriverId = valueDriverId;
		},

		openCategoryModal() {
			this.show_empty_state_modal = false;
			this.show_category_modal = true;
		},

		goToContact() {
			window.open("https://www.solenis.com/en/contact/contact-us/", "_blank");
		},

		dispatchModalStateChange(isOpen) {
			const event = new CustomEvent("modal-state-change", {
				detail: { isOpen },
			});
			document.dispatchEvent(event);
		},

		remap(number, inMin, inMax, outMin, outMax) {
			return ((number - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
		},

		async initialiseScreen() {
			try {
				this.loading = true;
				this.hotspots = [];
				this.visible_hotspots = [];
				this.image_ready = false;
				this.blur_loaded = false;
				this.blur_hash_image = null;
				this.show_empty_state_modal = false;

				this.loadedCategory = null;
				this.loadedSector = null;

				await this.$store.dispatch("processes/updateCurrentSector", {
					category_slug: this.$route.params.category,
					sector_slug: this.$route.params.sector,
				});

				if (!this.currentSector?.id) {
					this.loading = false;
					return;
				}

				this.loadedCategory = this.currentCategory;
				this.loadedSector = this.currentSector;

				const [hotspotsCheck, image] = await Promise.all([
					checkHotspots(this.currentSector.id),
					getImage(this.currentSector.id),
				]);

				this.show_empty_state_modal = !hotspotsCheck;
				await this.onGetImage(image);

				if (image.default_layer) {
					this.$store.dispatch("setDefaultLayer", image.default_layer);

					if (this.$route.name === "SectorScreen") {
						let layerId = image.default_layer.replace(/\s+/g, "-");
						// Fix "solution" to "solutions"
						if (layerId === "solution") {
							layerId = "solutions";
						}
						this.$router.replace({
							name: "LayerScreen",
							params: {
								...this.$route.params,
								layer: layerId,
							},
						});
					}
				}

				Promise.all([
					this.$store.dispatch("staticContent/setStaticContent"),
					this.setPortfolios(),
					this.setLanguages(),
					this.setValueDrivers(),
					this.setActiveValueDrivers({ valueDrivers: [] }),
				]).catch(() => {});
			} catch (error) {
				this.loading = false;
			}
		},
	},

	created() {
		this.hasShownNotice = localStorage.getItem("hasShownNotice") === "true";
		if (!this.hasShownNotice) {
			this.show_notice = true;
			localStorage.setItem("hasShownNotice", "true");
		}
	},
};
